<template>
    <div>
      <AdminHeader />
  
      <div id="stores">
  
        <!-- Page Header -->
        <div class="page-header">
          <div>
            <h5 class="page-title">Store List</h5>
            <p class="page-sub">Manage all your stores and locations</p>
          </div>
          <div class="page-actions">
  
            <!-- Search -->
            <div class="search-wrap">
              <font-awesome-icon icon="search" class="search-icon" />
              <input type="text" class="search-input" placeholder="Search stores..." v-model="query" />
            </div>
  
            <!-- Export -->
            <div class="dropdown">
              <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                <font-awesome-icon icon="download" />
                Export
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'stores', 'Stores')">
                    <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> Export as PDF
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click="reusableMixinToCSV('stores.csv')">
                    <font-awesome-icon icon="file-csv" class="me-2 text-success" /> Export as CSV
                  </a>
                </li>
              </ul>
            </div>
  
            <!-- Add Store -->
            <router-link
              :to="$url('/create-store')"
              class="btn-primary-action"
              :class="permission.create_store_management == 0 ? 'disabled' : ''"
            >
              <font-awesome-icon icon="plus" />
              Add Store
            </router-link>
  
            <!-- Bulk Delete -->
            <button
              class="btn-danger-action"
              :data-bs-toggle="permission.delete_store_management == 1 ? 'modal' : ''"
              :data-bs-target="permission.delete_store_management == 1 ? '#bulkModal' : ''"
              :class="permission.delete_store_management == 0 ? 'disabled' : ''"
              :disabled="selectedModel.length === 0"
            >
              <font-awesome-icon icon="trash" />
              Bulk Delete
              <span v-if="selectedModel.length > 0" class="badge-count">{{ selectedModel.length }}</span>
            </button>
  
          </div>
        </div>
  
        <!-- Card -->
        <div class="data-card">
  
          <!-- Loading -->
          <div v-if="loading" class="state-block">
            <div class="spinner-border text-primary" style="width:3rem; height:3rem;" role="status"></div>
            <p class="mt-3 text-muted">Loading stores...</p>
          </div>
  
          <!-- No Permission -->
          <div v-else-if="permission_status == 403 && !is_permission" class="state-block">
            <div class="state-icon state-icon--red">
              <font-awesome-icon icon="lock" />
            </div>
            <p class="state-title">Access Denied</p>
            <p class="state-sub">You don't have permission to access this resource.</p>
          </div>
  
          <!-- Empty -->
          <div v-else-if="details.length === 0" class="state-block">
            <div class="state-icon state-icon--blue">
              <font-awesome-icon icon="store" />
            </div>
            <p class="state-title">No stores yet</p>
            <p class="state-sub">Get started by adding your first store.</p>
            <router-link :to="$url('/create-store')" class="btn-primary-action mt-2" v-if="permission.create_store_management != 0">
              <font-awesome-icon icon="plus" /> Add Store
            </router-link>
          </div>
  
          <!-- Table -->
          <template v-else>
            <div class="table-toolbar">
              <span class="table-count">
                Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> stores
              </span>
              <div class="d-flex align-items-center gap-2">
                <label class="table-count">Rows per page:</label>
                <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option :value="pagination.total">All</option>
                </select>
              </div>
            </div>
  
            <div class="table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th class="col-check">
                      <input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" />
                    </th>
                    <th>Store Name</th>
                    <th>Address</th>
                    <th>State</th>
                    <th>Country</th>
                    <th>Phone Number</th>
                    <th>Status</th>
                    <th class="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(store, index) in details" :key="index" :class="selectedModel.includes(store.id) ? 'row-selected' : ''">
                    <td class="col-check">
                      <input type="checkbox" v-model="selectedModel" :value="store.id" class="form-check-input" />
                    </td>
                    <td>
                      <div class="store-name-cell">
                        <div class="store-avatar">
                          <font-awesome-icon icon="store" />
                        </div>
                        <span class="store-name">{{ store.store_name }}</span>
                      </div>
                    </td>
                    <td class="text-muted-cell">{{ store.address }}</td>
                    <td class="text-muted-cell">{{ store.city?.name }}</td>
                    <td class="text-muted-cell">{{ store.country?.name }}</td>
                    <td class="text-muted-cell">{{ store.phone_no }}</td>
                    <td>
                      <span :class="['status-badge', store.is_active ? 'status-badge--active' : 'status-badge--inactive']">
                        {{ store.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="col-actions">
                      <div class="action-btns">
                        <router-link
                          :to="permission.edit_store_management == 0 ? '#' : $url(`/edit-store/${store.id}`)"
                          class="action-btn action-btn--edit"
                          :class="permission.edit_store_management == 0 ? 'disabled' : ''"
                          title="Edit store"
                        >
                          <font-awesome-icon icon="pen" />
                        </router-link>
                        <button
                          class="action-btn action-btn--toggle"
                          :disabled="permission.edit_store_management != 1 || togglingId === store.id"
                          :class="permission.edit_store_management == 0 ? 'disabled' : ''"
                          :title="store.is_active ? 'Set Inactive' : 'Set Active'"
                          @click="toggleStatus(store)"
                        >
                          <font-awesome-icon v-if="togglingId === store.id" icon="spinner" spin />
                          <font-awesome-icon v-else :icon="store.is_active ? 'toggle-on' : 'toggle-off'" />
                        </button>
                        <button
                          class="action-btn action-btn--delete"
                          @click="getModalCurrentId(store.id)"
                          :data-bs-toggle="store.first_store !== store.created_at && permission.delete_store_management == 1 ? 'modal' : ''"
                          :data-bs-target="store.first_store !== store.created_at && permission.delete_store_management == 1 ? '#singleModal' : ''"
                          :disabled="store.first_store === store.created_at || permission.delete_store_management != 1"
                          title="Delete store"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
  
            <!-- Pagination -->
            <div class="table-footer">
              <pagination
                :pagination="pagination"
                @prev="tableData.page = pagination.currentPage - 1; fetchData()"
                @next="tableData.page = pagination.currentPage + 1; fetchData()"
              />
            </div>
  
          </template>
        </div>
      </div>
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="callSingleStoreDeleteButton" :modalText="'Are you sure you want to delete this store?'" :action="'deleteSingle'" />
      <BulkDeleteModal :modalId="'bulkModal'" @deleteBulk="callBulkDeletStoreButton" :modalText="'Are you sure you want to delete the selected stores?'" />
    </div>
  </template>
  
  <script>
  import AdminHeader from "./components/settingheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal from '../../../components/bulkDeleteModal.vue';
  import { reusableMixin } from "../../../lib/reusableMixin";
  import { useStore } from "vuex";
  import "jspdf-autotable";
  
  export default {
    components: { AdminHeader, SingleDeleteModal, BulkDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N', 'Stores', 'Address', 'State', 'Country', 'Phone Number'],
        details: [],
        loading: false,
        query: null,
        selectedModel: [],
        allSelected: false,
        togglingId: null,
        tableData: { page: 1,
          draw: 0, length: 10, search: '',
          column: 0, dir: 'desc', id: '',
          status: [0, 1, 2, 3], query: ''
        },
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: '', to: ''
        },
        toast: useToast(),
        currentID: null,
        permission_status: null,
        is_permission: true,
        store: useStore(),
      };
    },
  
    computed: {
      permission() {
        return this.store.state.getCurrentUserPermission;
      }
    },
  
    watch: {
      query() { this.fetchData(); }
    },
  
    created() {
      this.fetchData();
    },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((store, idx) => ({
          sn: idx + 1,
          store_name: store.store_name,
          address: store.address,
          city: store.city?.name,
          country: store.country?.name,
          phone_no: store.phone_no,
        }));
      },
  
      getModalCurrentId(id) { this.currentID = id; },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      fetchData(url = '/admin/store/user-stores') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.loading = false;
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.is_permission = false;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      callSingleStoreDeleteButton() {
        axios.delete(`/admin/store/${this.currentID}`)
          .then(() => {
            this.toast.success('Store deleted successfully');
            this.selectedModel = [];
            this.fetchData();
          })
          .catch(error => {
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
            if (error?.response?.status == 403) this.permission_status = 403;
          });
      },
  
      toggleStatus(store) {
        this.togglingId = store.id;
        axios.put(`/admin/store/${store.id}`, {
          store_name: store.store_name,
          address:    store.address,
          phone_no:   store.phone_no,
          is_active:  !store.is_active,
        })
          .then(() => {
            store.is_active = !store.is_active;
            this.toast.success(`Store ${store.is_active ? 'activated' : 'deactivated'} successfully.`);
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Failed to update status.');
          })
          .finally(() => { this.togglingId = null; });
      },

      callBulkDeletStoreButton() {
        axios.post('/admin/store/delete-bulk', { stores: this.selectedModel })
          .then(response => {
            this.toast.success(response.data.message);
            this.selectedModel = [];
            this.fetchData();
          })
          .catch(error => {
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  
  /* ── Page Header ── */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 20px;
  }
  
  .page-title {
    font-size: 20px;
    font-weight: 700;
    color: #1a2b45;
    margin: 0 0 3px;
  }
  
  .page-sub {
    font-size: 13px;
    color: #7a90b0;
    margin: 0;
  }
  
  .page-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  /* ── Search ── */
  .search-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .search-icon {
    position: absolute;
    left: 11px;
    color: #7a90b0;
    font-size: 13px;
    pointer-events: none;
  }
  .search-input {
    padding: 8px 12px 8px 32px;
    border: 1px solid #dde5f0;
    border-radius: 8px;
    font-size: 13px;
    color: #1a2b45;
    background: #fff;
    width: 220px;
    outline: none;
    transition: border-color 0.15s;
  }
  .search-input:focus { border-color: #004AAD; }
  .search-input::placeholder { color: #aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #004AAD;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
  }
  .btn-primary-action:hover { background: #003b8e; color: #fff; text-decoration: none; }
  .btn-primary-action.disabled { opacity: 0.5; pointer-events: none; }
  
  .btn-danger-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #fff0f0;
    color: #e03535;
    border: 1px solid #fac5c5;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
  }
  .btn-danger-action:hover:not(:disabled) { background: #ffe0e0; }
  .btn-danger-action:disabled { opacity: 0.45; cursor: not-allowed; }
  .btn-danger-action.disabled { opacity: 0.45; pointer-events: none; }
  
  .btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #fff;
    color: #1a2b45;
    border: 1px solid #dde5f0;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
  }
  .btn-outline:hover { background: #f4f7fb; }
  .btn-outline::after { display: none !important; }
  
  .badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e03535;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    width: 18px;
    height: 18px;
    border-radius: 50%;
  }
  
  /* ── Data Card ── */
  .data-card {
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 12px;
    overflow: hidden;
  }
  
  /* ── State blocks (loading/empty/error) ── */
  .state-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    text-align: center;
  }
  .state-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 16px;
  }
  .state-icon--blue { background: rgba(0,74,173,0.1); color: #004AAD; }
  .state-icon--red  { background: rgba(224,53,53,0.1); color: #e03535; }
  .state-title { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0 0 6px; }
  .state-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Table Toolbar ── */
  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-bottom: 1px solid #e4eaf2;
    flex-wrap: wrap;
    gap: 10px;
  }
  .table-count { font-size: 13px; color: #7a90b0; }
  .rows-select {
    border: 1px solid #dde5f0;
    border-radius: 7px;
    padding: 5px 10px;
    font-size: 13px;
    color: #1a2b45;
    background: #f8fafc;
    outline: none;
    cursor: pointer;
  }
  .rows-select:focus { border-color: #004AAD; }
  
  /* ── Table ── */
  .table-wrap { overflow-x: auto; }
  
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .data-table thead tr {
    background: #f8fafc;
  }
  .data-table thead th {
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #7a90b0;
    padding: 12px 16px;
    border-bottom: 1px solid #e4eaf2;
    white-space: nowrap;
  }
  
  .data-table tbody tr {
    border-bottom: 1px solid #f0f4f8;
    transition: background 0.12s;
  }
  .data-table tbody tr:last-child { border-bottom: none; }
  .data-table tbody tr:hover { background: #f8fafc; }
  .data-table tbody tr.row-selected { background: #edf4ff; }
  
  .data-table tbody td {
    padding: 13px 16px;
    font-size: 13.5px;
    vertical-align: middle;
  }
  
  .col-check { width: 44px; text-align: center; }
  .col-actions { width: 100px; text-align: center; }
  
  .text-muted-cell { color: #4a5e78; }
  
  /* Store name cell */
  .store-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .store-avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: rgba(0,74,173,0.1);
    color: #004AAD;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }
  .store-name {
    font-weight: 600;
    color: #1a2b45;
    text-transform: capitalize;
  }
  
  /* Action buttons */
  .action-btns {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .action-btn {
    width: 32px;
    height: 32px;
    border-radius: 7px;
    border: 1px solid #e4eaf2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    text-decoration: none;
    background: #fff;
  }
  .action-btn--edit  { color: #004AAD; }
  .action-btn--edit:hover:not(.disabled)  { background: #edf4ff; border-color: #c5d6f5; }
  .action-btn--toggle { color: #16a34a; }
  .action-btn--toggle:hover:not(:disabled):not(.disabled) { background: #f0fdf4; border-color: #bbf7d0; }
  .action-btn--toggle:disabled { color: #ccc; cursor: not-allowed; }
  .action-btn--delete { color: #e03535; }
  .action-btn--delete:hover:not(:disabled) { background: #fff0f0; border-color: #fac5c5; }
  .action-btn--delete:disabled { color: #ccc; cursor: not-allowed; border-color: #f0f0f0; }
  .action-btn.disabled { color: #ccc; pointer-events: none; }

  /* ── Status badge ── */
  .status-badge           { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
  .status-badge--active   { background: #f0fdf4; color: #16a34a; }
  .status-badge--inactive { background: #f8fafc; color: #7a90b0; }
  
  /* ── Table Footer ── */
  .table-footer {
    padding: 14px 20px;
    border-top: 1px solid #e4eaf2;
    display: flex;
    justify-content: flex-end;
  }
  
  /* ── Dropdown ── */
  .dropdown-menu {
    border: 1px solid #e4eaf2;
    border-radius: 9px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    padding: 6px;
    min-width: 170px;
  }
  .dropdown-item {
    border-radius: 6px;
    font-size: 13px;
    padding: 8px 12px;
    color: #1a2b45;
    transition: background 0.12s;
  }
  .dropdown-item:hover { background: #f4f7fb; }
  
  /* ── Responsive ── */
  @media (max-width: 767px) {
    .page-header   { flex-direction: column; align-items: stretch; }
    .page-actions  { flex-direction: column; }
    .search-input  { width: 100%; }
    .btn-primary-action,
    .btn-danger-action,
    .btn-outline   { width: 100%; justify-content: center; }
  }
  </style>